// Pauses the hero's autoplay/loop background video while it's scrolled out
// of the viewport, and resumes it on scroll back in -- the same rationale
// as modules/swiper-viewport-autoplay.js and
// modules/recommended-glow-viewport.js, applied to video decode instead of
// a CSS/JS animation: a looping <video autoplay> keeps decoding frames
// indefinitely once started, with nothing to stop it once the visitor has
// simply scrolled past the hero section.
//
// video.pause()/.play() are native HTMLMediaElement methods, not a
// third-party plugin's own API -- unlike swiper-viewport-autoplay.js's
// .autoplay.pause() (confirmed broken on that library), there's no reason
// to suspect these don't reliably start/stop decode, so this uses a plain
// IntersectionObserver without the extra self-correcting poll the other
// two modules needed to work around Swiper's specific bug.
const VIEWPORT_MARGIN_PX = 200;

export default function initHeroVideoViewport() {
    if (typeof window.IntersectionObserver !== 'function') {
        return;
    }

    // Excludes a YouTube/Vimeo embed (.hero__video--embed, JCTCTAS-5): that's
    // an <iframe>, not an HTMLMediaElement -- it has no .play()/.pause() for
    // this observer to call, and pausing a cross-origin embed's decode
    // requires that provider's own postMessage JS API, which this module
    // deliberately doesn't take on just to cover an off-screen scroll case.
    const videos = document.querySelectorAll('.hero__video:not(.hero__video--embed)');
    if (!videos.length) {
        return;
    }

    // The project's global reduced-motion rule (base/_base.scss) only
    // neutralizes CSS animation/transition durations -- it cannot stop
    // <video autoplay loop> playback. This module is what actually resumes
    // playback (via .play()) as the hero scrolls into view, so it's the
    // right place to also refuse to ever start it for a visitor who
    // prefers reduced motion -- common.js's initHeroVideo() separately
    // pauses whatever the native `autoplay` attribute already started
    // before this script ran.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
    }

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                const video = entry.target;
                if (entry.isIntersecting) {
                    video.play().catch(() => {});
                } else {
                    video.pause();
                }
            });
        },
        { rootMargin: `${VIEWPORT_MARGIN_PX}px 0px` }
    );

    videos.forEach((video) => {
        observer.observe(video);
    });
}
